import { useUserContext } from '@vp/ubik-context'
import { getError, logger } from '@shared/lib/logger'
import { trackEvent } from '@shared/lib/tracking'
import { attempt } from '@shared/utils'
import type { C2CServiceResponseData, CaseCreationResponse, ChannelActionResult, Intent } from '@vp/contact-channels'
import { useCallMeActions, useEmailActions, useIntents } from '@vp/contact-channels'
import { useCallback } from 'react'
import { useChannelsAvailability } from '@vp/contact-channels'
import { TRACKING_EVENTS } from '../config/tracking.config'
import type { CallbackFormData, ContactFormData, EmailFormData } from '../types/form-data.types'
import { buildDescription } from '../utils/submission.utils'
import { useAppData } from '@app/providers'
import { getExperimentVariation, isOobhExperimentActive } from '../utils/chat.utils'
import { useChatStatus } from './use-chat-status'

type SubmissionResult = ChannelActionResult<C2CServiceResponseData | CaseCreationResponse>

const COMPONENT = 'useSubmitContactRequest'

export const useSubmitContactRequest = () => {
  const { channelInfo } = useChannelsAvailability(['callMe', 'phone'])
  const isCallMeAvailable = channelInfo.callMe?.isAvailable && channelInfo.callMe?.data?.isScheduleOpen
  const { createCallbackAction } = useCallMeActions()
  const { createCaseAction } = useEmailActions()
  const { data: intents } = useIntents()
  const { ordersData } = useAppData()
  const { experiments, locale } = useUserContext()
  const isOobhActive = isOobhExperimentActive(locale, experiments)
  const { chatStatus } = useChatStatus(locale, isOobhActive)

  const buildOrderNumberString = (formData: ContactFormData): string => {
    const selectedOrderNumbers = [
      ...new Set(
        formData.selectedLineItemIds
          .map((itemId) => ordersData?.find((o) => o.orderData.lineItems.some((li) => li.id === itemId))?.orderNumber)
          .filter((n): n is string => !!n),
      ),
    ]
    const manual = formData.orderNumber?.trim()
    return [...selectedOrderNumbers, ...(manual ? [manual] : [])].join(', ')
  }

  const submitCallback = (formData: CallbackFormData, intent?: Intent): Promise<SubmissionResult> =>
    createCallbackAction.mutateAsync({
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phone,
      countryCode: formData.countryCode,
      country: formData.country,
      category: intent?.value || formData.intentId,
      parentCategory: intent?.parent || formData.parentCategory || 'Print',
      isOptOutEnabled: true,
      optInRecording: formData.optInRecording,
      orderNumber: buildOrderNumberString(formData) || undefined,
      summary: buildDescription(formData) || undefined,
    })

  const submitEmail = async (formData: EmailFormData, intent?: Intent): Promise<SubmissionResult> => {
    const result = await createCaseAction.mutateAsync({
      caseType: 'email',
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      subject: intent?.value || formData.intentId,
      companyName: '',
      phoneNumber: '',
      comment: buildDescription(formData),
      orderNumber: buildOrderNumberString(formData),
      assets: formData.attachments.length > 0 ? formData.attachments : undefined,
    })

    if (result.success) {
      const caseId = result.data?.caseId
      trackEvent(TRACKING_EVENTS.CARE_CASE_SUBMITTED, {
        category: 'Global Email',
        pageSection: 'Help Center',
        pageStage: 'Customer Care',
        pageName: 'Help Center:Email Form',
        label: 'Email Initiated',
        ...(caseId && { caseId }),
        eventDetail: isCallMeAvailable ? 'BH;' : '',
      })
      if (isOobhActive && !chatStatus?.isChatEnabled) {
        trackEvent(TRACKING_EVENTS.EXPERIMENT_CLICKED, {
          category: 'Global Email',
          pageSection: 'Help Center',
          pageStage: 'Customer Care',
          pageName: 'Help Center:Email Form',
          label: 'Email Initiated',
          eventDetail: 'Email Initiated',
          experimentDetail: 'va_24x7_oobh_email_created',
          experimentName: 'vista_assistant_always_on',
          variationName: getExperimentVariation(locale),
        })
      }
    }

    return result
  }

  const submitContactRequest = useCallback(
    async (formData: ContactFormData): Promise<SubmissionResult> => {
      const startedAt = Date.now()
      const intent = intents?.find((i) => i.id === formData.intentId)

      logger.info('Contact request submission started', {
        action: 'contact_request_submit_started',
        component: COMPONENT,
        channel: formData.channel,
        intentId: formData.intentId,
        intentResolved: !!intent,
        attachmentCount: formData.channel === 'email' ? formData.attachments.length : 0,
        hasOrderNumber: Boolean(formData.selectedLineItemIds?.length) || Boolean(formData.orderNumber?.trim()),
        hasDescription: Boolean(formData.description),
      })

      const submission = formData.channel === 'callback' ? submitCallback(formData, intent) : submitEmail(formData, intent)
      const { data: result, error } = await attempt(submission)
      const durationMs = Date.now() - startedAt

      if (error) {
        logger.error(
          'Contact request submission failed',
          { action: 'contact_request_submit_failed', component: COMPONENT, channel: formData.channel, intentId: formData.intentId, durationMs },
          getError(error),
        )
        throw error
      }

      if (!result.success) {
        logger.warn('Contact request submission returned unsuccessful result', {
          action: 'contact_request_submit_unsuccessful',
          component: COMPONENT,
          channel: formData.channel,
          intentId: formData.intentId,
          durationMs,
          hasErrorMessage: Boolean(result.error),
          errorMessage: result.error,
        })
        return result
      }

      logger.info('Contact request submission succeeded', {
        action: 'contact_request_submit_succeeded',
        component: COMPONENT,
        channel: formData.channel,
        intentId: formData.intentId,
        durationMs,
      })

      return result
    },
    [createCallbackAction, createCaseAction, intents],
  )

  return { submitContactRequest }
}
